import { useRef, useState } from 'react';
import { trackSidebarClick } from '@/lib/posthog-events';
import { confirmLeaveIfDirty, setHasUnsavedChanges } from '@/lib/unsaved-changes';
import type { NavItem } from './navigation-items';

interface UseNavigationStateOptions {
  onNavigate: (path: string) => void;
  onAIChatOpen?: () => void;
  // Called after any navigation/AI-chat-open action resolves -- the
  // mobile drawer uses this to close itself; the desktop Sidebar (which
  // has no "closing" concept beyond its own submenu) leaves it unset.
  onAfterAction?: () => void;
}

// BUG-16 (2026-09-19): the click/open-submenu/double-click-to-navigate
// logic used to live only inside sidebar-navigation.tsx -- extracted so
// mobile-navigation.tsx can drive the exact same nav tree without
// duplicating (and inevitably drifting from) this behavior.
export function useNavigationState({ onNavigate: rawOnNavigate, onAIChatOpen, onAfterAction }: UseNavigationStateOptions) {
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const lastClickTime = useRef<{ [key: string]: number }>({});
  const doubleClickDelay = 300;

  // Every real navigation attempt (desktop Sidebar and the mobile drawer
  // both drive this same hook) goes through here first -- if some page
  // registered unsaved changes, this is where that gets caught, same
  // confirm dialog/copy as Settings' own tab switcher. Checked BEFORE any
  // of this hook's own UI state changes (closing an open submenu, closing
  // the mobile drawer via onAfterAction), so cancelling really does leave
  // everything exactly as it was -- nothing here happens on a "no".
  const onNavigate = (path: string) => {
    if (!confirmLeaveIfDirty()) return false;
    setHasUnsavedChanges(false);
    rawOnNavigate(path);
    return true;
  };

  const handleItemClick = (item: NavItem) => {
    if (item.action === 'project-setup' && onAIChatOpen) {
      trackSidebarClick('ai_chat', 'widget_open');
      onAIChatOpen();
      onAfterAction?.();
      return;
    }

    const now = Date.now();
    const lastClick = lastClickTime.current[item.title] || 0;
    const isDoubleClick = now - lastClick < doubleClickDelay;
    lastClickTime.current[item.title] = now;

    if (item.submenu) {
      if (isDoubleClick && item.href) {
        trackSidebarClick(item.title, 'double_click_navigate');
        if (!onNavigate(item.href)) return;
        setOpenMenus([]);
        onAfterAction?.();
      } else {
        trackSidebarClick(item.title, 'menu_toggle');
        setOpenMenus((prev) => (prev.includes(item.title) ? prev.filter((menu) => menu !== item.title) : [item.title]));
      }
    } else {
      trackSidebarClick(item.title, 'direct_navigate');
      if (item.href) {
        if (!onNavigate(item.href)) return;
        setOpenMenus([]);
        onAfterAction?.();
      }
    }
  };

  const handleSubmenuClick = (e: React.MouseEvent, item: NavItem, parentTitle: string) => {
    e.stopPropagation();
    trackSidebarClick(parentTitle, item.title);
    if (item.href) {
      if (!onNavigate(item.href)) return;
      setOpenMenus([]);
      onAfterAction?.();
    }
  };

  return { openMenus, handleItemClick, handleSubmenuClick };
}
